/**
 * Which camera to take pictures with.
 *
 * macOS can offer an iPhone through Continuity Camera as an ordinary video input. Phones may also offer
 * individual lenses as separate inputs. The browser hides device *names* until a camera has been allowed,
 * so a picker is
 * offered after the first capture has been granted, from the devices the browser lists.
 *
 * The choice is remembered per browser (`perfume.camera.device`), like the balance's port.
 */

export const CAMERA_STORAGE_KEY = "perfume.camera.device";

export type CameraFacing = "user" | "environment";

export interface CameraDevice {
  /** `deviceId`, or "" for the browser's default. */
  id: string;
  label: string;
}

export interface CameraChoice extends CameraDevice {
  /** Another automatic combination of the same rear lenses, available in the expanded picker. */
  alternative: boolean;
}

type PhoneCameraKind = "front" | "main" | "ultra-wide" | "telephoto" | "triple" | "dual-wide" | "dual";

/** Recognize the names iOS exposes in English; leave unknown/localized camera names intact. */
function phoneCameraKind(label: string): PhoneCameraKind | null {
  const name = label.trim().toLowerCase().replace(/\s+/g, " ");
  if (name === "front camera") return "front";
  const match =
    /^(?:back|rear) (?:(triple|dual wide|dual|ultra wide|ultrawide|wide angle|wide|telephoto) )?camera$/.exec(name);
  if (!match) return null;
  switch (match[1]) {
    case "triple":
      return "triple";
    case "dual wide":
      return "dual-wide";
    case "dual":
      return "dual";
    case "ultra wide":
    case "ultrawide":
      return "ultra-wide";
    case "telephoto":
      return "telephoto";
    default:
      return "main";
  }
}

const CAMERA_RANK: Record<PhoneCameraKind, number> = {
  triple: 0,
  "dual-wide": 1,
  dual: 2,
  main: 10,
  "ultra-wide": 20,
  telephoto: 30,
  front: 40,
};
const CAMERA_NAMES: Record<PhoneCameraKind, string> = {
  triple: "Back — automatic (triple)",
  "dual-wide": "Back — automatic (dual wide)",
  dual: "Back — automatic (dual)",
  main: "Back — main",
  "ultra-wide": "Back — ultra-wide",
  telephoto: "Back — telephoto",
  front: "Front camera",
};

/**
 * iOS lists physical lenses alongside virtual combinations (Triple, Dual Wide and Dual). Offer one
 * automatic mode plus the physical lenses first. Every device remains available; neither device ids
 * nor zoom factors tell us how many physical cameras exist on another phone.
 */
export function cameraChoices(devices: CameraDevice[]): CameraChoice[] {
  const seen = new Set<string>();
  const choices = devices
    .filter((device) => {
      if (seen.has(device.id)) return false;
      seen.add(device.id);
      return true;
    })
    .map((device) => ({ device, kind: phoneCameraKind(device.label) }))
    .sort((a, b) => (a.kind ? CAMERA_RANK[a.kind] : 50) - (b.kind ? CAMERA_RANK[b.kind] : 50));
  const automatic = choices.find(({ kind }) => kind === "triple" || kind === "dual-wide" || kind === "dual");
  return choices.map(({ device, kind }) => {
    const virtual = kind === "triple" || kind === "dual-wide" || kind === "dual";
    const preferred = virtual && automatic?.device.id === device.id;
    return {
      ...device,
      label: preferred ? "Back — automatic" : kind ? CAMERA_NAMES[kind] : device.label,
      alternative: virtual && !preferred,
    };
  });
}

const ENVIRONMENT = /back|rear|environment|world/i;

/** The cameras the browser offers, with a readable name for each. */
export async function listCameras(): Promise<CameraDevice[]> {
  const media = typeof navigator === "undefined" ? undefined : navigator.mediaDevices;
  if (!media || typeof media.enumerateDevices !== "function") return [];
  const found: MediaDeviceInfo[] = await media.enumerateDevices();
  return found
    .filter((device) => device.kind === "videoinput")
    .map((device, index) => ({
      id: device.deviceId,
      label: device.label || (device.deviceId === "default" ? "Default camera" : `Camera ${index + 1}`),
    }))
    .filter((device) => device.id !== "");
}

export function loadCameraId(): string | null {
  try {
    return window.localStorage.getItem(CAMERA_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveCameraId(id: string): void {
  try {
    window.localStorage.setItem(CAMERA_STORAGE_KEY, id);
  } catch {
    // Storage blocked (private mode): the choice lasts for this visit.
  }
}

/** The device a remembered id still points at, or null when the camera has gone away. */
export function knownCamera(devices: CameraDevice[], id: string | null): string | null {
  if (!id) return null;
  return devices.some((device) => device.id === id) ? id : null;
}

/** Prefer the remembered camera, else the browser's default. */
export function preferredCamera(devices: CameraDevice[], remembered: string | null): string | null {
  const kept = knownCamera(devices, remembered);
  if (kept) return kept;
  return (devices.find((device) => device.id === "default") ?? devices[0])?.id ?? null;
}

/** A stable label for one camera, with the rear camera marked on a phone. */
export function cameraLabel(device: CameraDevice, facing?: string): string {
  if (!device.label) return "Camera";
  return facing && ENVIRONMENT.test(facing) ? `${device.label} (rear)` : device.label;
}

/** `deviceId` constraints for `getUserMedia`: the chosen camera, else the rear one where there is one. */
export function videoConstraints(deviceId: string | null, facing: CameraFacing = "environment"): MediaTrackConstraints {
  // Ideal values do not reject smaller cameras. Ask high, then refine to the track's reported maximum.
  const resolution = { width: { ideal: 8192 }, height: { ideal: 8192 } };
  if (deviceId && deviceId !== "default") {
    return { deviceId: { exact: deviceId }, ...resolution };
  }
  return { facingMode: { ideal: facing }, ...resolution };
}
